import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { useTranslation } from "react-i18next";

import Uppy from "@uppy/core";
import "@uppy/core/css/style.css";

import {
  completeUpload,
  createLotDocumentDraft,
  deleteLotDocument,
  downloadLotDocument,
  listDraftFiles,
  listLotDocuments,
  presignDraftUpload,
  presignLotUpload,
  type CreateLotDocumentUploadRequest,
  type LotDocument,
  type LotDocumentDraft,
  type LotDraftFile,
} from "../api/lotDocuments";
import { useAuth } from "../../../providers/AuthProvider";
import { useToast } from "../../../providers/ToastProvider";
import {
  FileArchive,
  FileDoc,
  FileImage,
  FilePdf,
  FileText,
  UploadSimple,
} from "@phosphor-icons/react";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("LotFileManager");

interface LotFileManagerProps {
  lotId?: string;
  workspaceId?: string;
  mode: "read" | "edit";
  draftToken?: string;
  onDraftToken?: (token: string) => void;
  allowDraftUploads?: boolean;
}

type ListItem = LotDocument | LotDraftFile;
type UploadableFile = { id: string; name: string; data: Blob };

const allowedFileTypes = [
  ".pdf",
  ".tiff",
  ".tif",
  ".jpg",
  ".jpeg",
  ".png",
  ".heic",
  ".doc",
  ".docx",
  ".zip",
];

const formatBytes = (bytes?: number) => {
  if (!bytes || bytes < 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let idx = 0;
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[idx]}`;
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const documentTypeOptions: { value: CreateLotDocumentUploadRequest["document_type"]; label: string }[] = [
  { value: "title_scan", label: "Title scan" },
  { value: "tax_declaration", label: "Tax declaration" },
  { value: "survey_plan", label: "Survey plan" },
  { value: "payment_receipt", label: "Payment receipt" },
  { value: "supporting_document", label: "Supporting" },
  { value: "other", label: "Other" },
];

const containerClasses = "flex flex-col gap-6";
const dropzoneClasses = "group relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-10 transition hover:border-sky-400 hover:bg-sky-50 cursor-pointer dark:border-slate-700 dark:bg-slate-800/30 dark:hover:border-sky-600 dark:hover:bg-sky-900/20";
const dropzoneActiveClasses = "border-sky-500 bg-sky-50 dark:border-sky-500 dark:bg-sky-900/30 ring-2 ring-sky-500/20";
const dropzoneTextClasses = "text-sm font-medium text-slate-700 group-hover:text-sky-700 dark:text-slate-300 dark:group-hover:text-sky-300";
const dropzoneSubtextClasses = "text-xs text-slate-500 dark:text-slate-400";
const dropzoneIconClasses = "h-10 w-10 text-slate-400 transition group-hover:text-sky-500 group-hover:scale-110 dark:text-slate-500 dark:group-hover:text-sky-400";

const queueClasses = "bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden dark:bg-slate-900 dark:border-slate-800";
const queueHeaderClasses = "bg-slate-50 px-4 py-3 border-b border-slate-200 dark:bg-slate-800 dark:border-slate-800";
const queueListClasses = "divide-y divide-slate-100 dark:divide-slate-800";
const queueRowClasses = "flex items-center justify-between p-4 gap-4";
const progressClasses = "fixed bottom-4 right-4 w-80 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden z-50 dark:bg-slate-900 dark:border-slate-800";
const errorClasses = "rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 border border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-900/50";
const loadingClasses = "text-center py-8 text-sm text-slate-500 italic dark:text-slate-400";
const emptyClasses = "text-center py-12 text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50 dark:text-slate-400 dark:border-slate-700 dark:bg-slate-800/20";
const listContainerClasses = "bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden dark:bg-slate-900 dark:border-slate-800";
const listInnerClasses = "divide-y divide-slate-100 dark:divide-slate-800";
const fileRowClasses = "flex items-center justify-between p-4 transition hover:bg-slate-50 dark:hover:bg-slate-800/50";
const fileMetaClasses = "flex items-center gap-4 overflow-hidden";
const iconContainerBase = "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg";
const iconBase = "h-6 w-6";
const actionClasses = "flex items-center gap-2 shrink-0";
const selectClasses = "block w-full rounded-lg border-0 py-1.5 pl-3 pr-8 text-xs font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 dark:text-slate-200 dark:ring-slate-700 dark:bg-slate-800";
const typeBadgeClasses = "inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-500/10 dark:bg-slate-400/10 dark:text-slate-400 dark:ring-slate-400/20";

const primaryButtonClasses = "pm-btn pm-btn-primary";
const secondaryButtonClasses = "pm-btn pm-btn-surface pm-btn-xs";
const dangerButtonClasses = "pm-btn pm-btn-danger pm-btn-xs";
const ghostButtonClasses = "pm-btn pm-btn-ghost pm-btn-xs";

const FileIcon = ({ name, mimeType }: { name?: string; mimeType?: string }) => {
  const lowerExt = name?.includes(".") ? name.split(".").pop()?.toLowerCase() : undefined;
  
  if (mimeType?.includes("pdf") || lowerExt === "pdf") {
    return (
      <div className={`${iconContainerBase} bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400`}>
        <FilePdf weight="duotone" className={iconBase} aria-hidden />
      </div>
    );
  }
  if ((mimeType && mimeType.startsWith("image/")) || ["jpg", "jpeg", "png", "tif", "tiff", "heic"].includes(lowerExt || "")) {
    return (
      <div className={`${iconContainerBase} bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400`}>
        <FileImage weight="duotone" className={iconBase} aria-hidden />
      </div>
    );
  }
  if (
    (mimeType && mimeType.includes("word")) ||
    ["doc", "docx"].includes(lowerExt || "")
  ) {
    return (
      <div className={`${iconContainerBase} bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400`}>
        <FileDoc weight="duotone" className={iconBase} aria-hidden />
      </div>
    );
  }
  if (
    (mimeType && mimeType.includes("zip")) ||
    ["zip", "gz", "tar", "rar", "7z"].includes(lowerExt || "")
  ) {
    return (
      <div className={`${iconContainerBase} bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400`}>
        <FileArchive weight="duotone" className={iconBase} aria-hidden />
      </div>
    );
  }
  return (
    <div className={`${iconContainerBase} bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400`}>
      <FileText weight="duotone" className={iconBase} aria-hidden />
    </div>
  );
};

const FilePreview = ({ file, name, mimeType }: { file?: Blob | File; name?: string; mimeType?: string }) => {
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (file && (mimeType?.startsWith("image/") || name?.match(/\.(jpg|jpeg|png|webp)$/i))) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
    return undefined;
  }, [file, mimeType, name]);

  if (preview) {
    return (
      <img
        src={preview}
        alt={name}
        className="h-10 w-10 rounded-lg object-cover ring-1 ring-black/5 dark:ring-white/10"
      />
    );
  }
  return <FileIcon name={name} mimeType={mimeType} />;
};

export function LotFileManager({
  lotId,
  workspaceId,
  mode,
  draftToken,
  onDraftToken,
  allowDraftUploads = false,
}: LotFileManagerProps) {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ListItem[]>([]);
  const [draft, setDraft] = useState<LotDocumentDraft | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, { name: string; percent: number }>>({});
  const [lastSelectedType, setLastSelectedType] = useState<CreateLotDocumentUploadRequest["document_type"]>(
    "title_scan",
  );
  const [docToDelete, setDocToDelete] = useState<LotDocument | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const progressToastRef = useRef<string | null>(null);
  const progressPanelRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sanitizeAttachmentKey = useCallback((name: string) => {
    const maxLength = 64;
    const normalize = (value: string) =>
      value
        .replace(/[^a-zA-Z0-9._-]+/g, "-")
        .replace(/\.+/g, ".")
        .replace(/-+/g, "-")
        .replace(/^[-_.]+/, "")
        .replace(/[-_.]+$/, "");

    const trimmed = name.trim();
    if (!trimmed) return "file";

    const lastDot = trimmed.lastIndexOf(".");
    const hasExt = lastDot > 0 && lastDot < trimmed.length - 1;
    const rawBase = hasExt ? trimmed.slice(0, lastDot) : trimmed;
    const rawExt = hasExt ? trimmed.slice(lastDot + 1) : "";

    const safeBase = normalize(rawBase) || "file";
    const safeExt = normalize(rawExt);
    const extPart = safeExt ? `.${safeExt}` : "";

    const maxBaseLength = Math.max(1, maxLength - extPart.length);
    const truncatedBase = safeBase.slice(0, maxBaseLength);

    return `${truncatedBase}${extPart}`.slice(0, maxLength);
  }, []);

  const draftKey = draftToken || draft?.token;
  const canUpload = Boolean(lotId || (allowDraftUploads && draftKey));

  const uppy = useMemo(
    () =>
      new Uppy({
        autoProceed: false,
        allowMultipleUploadBatches: true,
        restrictions: {
          maxNumberOfFiles: 50,
          maxFileSize: 25 * 1024 * 1024,
          allowedFileTypes,
        },
      }),
    [],
  );

  type QueuedFileSnapshot = {
    id: string;
    name: string;
    size?: number;
    type?: string;
    data?: Blob | File;
    meta: Record<string, unknown>;
  };

  const mapQueuedFiles = useCallback(
    () =>
      uppy.getFiles().map<QueuedFileSnapshot>((file) => ({
        id: file.id,
        name: file.name,
        size: file.size ?? undefined,
        type: file.type,
        data: file.data as Blob | File | undefined,
        meta: file.meta as Record<string, unknown>,
      })),
    [uppy],
  );

  const [queuedFiles, setQueuedFiles] = useState<QueuedFileSnapshot[]>(mapQueuedFiles);

  useEffect(() => {
    let active = true;
    async function ensureDraft() {
      if (!allowDraftUploads) return;
      if (mode !== "edit" || lotId || draftKey || !workspaceId || !accessToken) return;
      try {
        const newDraft = await createLotDocumentDraft(accessToken, workspaceId);
        if (!active) return;
        setDraft(newDraft);
        onDraftToken?.(newDraft.token);
      } catch (err) {
        logger.error("Failed to create draft", err);
        if (!active) return;
        setError("Failed to start document draft");
      }
    }
    ensureDraft();
    return () => {
      active = false;
    };
  }, [accessToken, allowDraftUploads, draftKey, lotId, mode, onDraftToken, workspaceId]);

  const fetchItems = useCallback(async () => {
    if (!accessToken) return;
    if (lotId) {
      const docs = await listLotDocuments(accessToken, lotId);
      setItems(docs);
    } else if (draftKey) {
      const files = await listDraftFiles(accessToken, draftKey);
      setItems(files);
    }
  }, [accessToken, draftKey, lotId]);

  useEffect(() => {
    setError(null);
    setLoading(true);
    fetchItems()
      .catch((err) => {
        logger.error("Failed to load documents", err);
        setError("Unable to load documents");
      })
      .finally(() => setLoading(false));
  }, [fetchItems]);

  useEffect(() => {
    uppy.on("file-added", (file) => {
      if (!accessToken) {
        uppy.removeFile(file.id);
        return;
      }
      const ext = file.extension || "";
      if (!ext) {
        uppy.info("Filename must include an extension", "error", 4000);
        uppy.removeFile(file.id);
        return;
      }
      uppy.setFileMeta(file.id, { documentType: lastSelectedType });
      setLastSelectedType((prev) => prev);
    });
    const updateQueue = () => setQueuedFiles(mapQueuedFiles());
    uppy.on("file-removed", updateQueue);
    uppy.on("file-added", updateQueue);
    // @ts-expect-error event name not in typing but emitted by Uppy
    uppy.on("file-meta-update", updateQueue);
    return () => {
      uppy.off("file-removed", updateQueue);
      uppy.off("file-added", updateQueue);
      // @ts-expect-error event name not in typing but emitted by Uppy
      uppy.off("file-meta-update", updateQueue);
    };
  }, [accessToken, lastSelectedType, mapQueuedFiles, uppy]);

  const updateUploadProgress = (fileId: string, name: string, percent: number) => {
    setUploadProgress((prev) => ({
      ...prev,
      [fileId]: { name, percent: Math.min(100, Math.max(0, percent)) },
    }));
  };

  const clearUploadProgress = (fileId: string) => {
    setUploadProgress((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  };

  const uploadWithProgress = (
    url: string,
    file: UploadableFile,
    mimeType: string,
  ) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", mimeType);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          updateUploadProgress(file.id, file.name, Math.round((event.loaded / event.total) * 100));
        } else {
          updateUploadProgress(file.id, file.name, 10);
        }
      };
      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          updateUploadProgress(file.id, file.name, 100);
          resolve();
        } else {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      };
      xhr.send(file.data);
    });

  const handleUpload = async () => {
    if (!accessToken) return;
    if (!lotId && !allowDraftUploads) {
      setError("Create the lot before uploading documents.");
      return;
    }
    if (!lotId && allowDraftUploads && !draftKey) {
      setError("Draft or lot context is required to upload");
      return;
    }
    setUploading(true);
    setError(null);

    const files = uppy.getFiles();
    for (const file of files) {
      const docType =
        (file.meta.documentType as CreateLotDocumentUploadRequest["document_type"]) || lastSelectedType;
      const fileData = file.data instanceof Blob ? file.data : new Blob();
      const uploadableFile: UploadableFile = {
        id: file.id,
        name: file.name,
        data: fileData,
      };
      const payload: CreateLotDocumentUploadRequest = {
        document_type: docType,
        attachment_key: sanitizeAttachmentKey(file.name),
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        byte_size: file.size ?? fileData.size,
      };
      try {
        const presign = lotId
          ? await presignLotUpload(accessToken, lotId, payload)
          : await presignDraftUpload(accessToken, draftKey ?? "", payload);

        await uploadWithProgress(presign.url, uploadableFile, payload.mime_type);

        const buffer = await uploadableFile.data.arrayBuffer();
        const hash = await crypto.subtle.digest("SHA-256", buffer);
        const checksum = Array.from(new Uint8Array(hash))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");

        const finalize = await completeUpload(accessToken, presign.upload_id, {
          upload_id: presign.upload_id,
          checksum,
          byte_size: payload.byte_size,
          mime_type: payload.mime_type,
          filename: payload.filename,
        });

        uppy.setFileState(file.id, {
          progress: {
            uploadStarted: Date.now(),
            uploadComplete: true,
            percentage: 100,
            bytesUploaded: payload.byte_size,
            bytesTotal: payload.byte_size,
          },
        });

        if (finalize.document) {
          setItems((prev) => [...prev, finalize.document!]);
        } else if (finalize.draft_file) {
          setItems((prev) => [...prev, finalize.draft_file!]);
        }
        clearUploadProgress(file.id);
        uppy.removeFile(file.id);
        setQueuedFiles(mapQueuedFiles());
      } catch (err) {
        logger.error("Upload failed", err);
        setError("Upload failed");
        const message = err instanceof Error ? err.message : "Upload failed";
        uppy.setFileState(file.id, {
          error: message,
        });
        clearUploadProgress(file.id);
      }
    }

    setUploading(false);
    await fetchItems().catch((err) => {
      logger.error("Failed to refresh documents", err);
    });
  };

  useEffect(() => {
    if (uploading && queuedFiles.length > 0) {
      if (progressToastRef.current) {
        toast.dismiss(progressToastRef.current);
      }
      progressToastRef.current = toast.info(
        t("workspaces.lot_detail.documents_uploading_toast", {
          count: queuedFiles.length,
        }),
        {
          duration: 0,
        },
      );
      progressPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (!uploading && progressToastRef.current) {
      toast.dismiss(progressToastRef.current);
      progressToastRef.current = null;
      toast.success(t("workspaces.lot_detail.documents_upload_complete"));
    }
  }, [queuedFiles.length, t, toast, uploading]);

  useEffect(() => {
    return () => {
      if (progressToastRef.current) {
        toast.dismiss(progressToastRef.current);
        progressToastRef.current = null;
      }
    };
  }, [toast]);

  const handleQueuedTypeChange = (fileId: string, value: CreateLotDocumentUploadRequest["document_type"]) => {
    const file = uppy.getFile(fileId);
    if (!file) return;
    setLastSelectedType(value);
    uppy.setFileMeta(fileId, { ...file.meta, documentType: value });
    setQueuedFiles(mapQueuedFiles());
  };

  const handleRemoveQueuedFile = (fileId: string) => {
    uppy.removeFile(fileId);
    setQueuedFiles(mapQueuedFiles());
  };

  const handleDownload = async (doc: LotDocument) => {
    if (!accessToken || !lotId) return;
    try {
      const { url } = await downloadLotDocument(accessToken, lotId, doc.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      logger.error("Download failed", err);
      setError("Download failed");
    }
  };

  const handleDelete = async (doc: LotDocument) => {
    if (!accessToken || !lotId) return;
    try {
      setDeletingDocId(doc.id);
      await deleteLotDocument(accessToken, lotId, doc.id);
      setItems((prev) => prev.filter((item) => item.id !== doc.id));
    } catch (err) {
      logger.error("Delete failed", err);
      setError("Delete failed");
    } finally {
      setDeletingDocId(null);
      setDocToDelete(null);
    }
  };

  const renderActions = (item: ListItem) => {
    if (lotId && "lot_id" in item && mode === "edit") {
      return (
        <div className={actionClasses}>
          <button type="button" className={secondaryButtonClasses} onClick={() => handleDownload(item as LotDocument)}>
            Download
          </button>
          <button type="button" className={dangerButtonClasses} onClick={() => setDocToDelete(item as LotDocument)}>
            Delete
          </button>
        </div>
      );
    }
    if (lotId && "lot_id" in item) {
      return (
        <div className={actionClasses}>
          <button type="button" className={secondaryButtonClasses} onClick={() => handleDownload(item as LotDocument)}>
            Download
          </button>
        </div>
      );
    }
    return null;
  };

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      files.forEach((file) => {
        try {
          uppy.addFile({
            source: "react-dropzone",
            name: file.name,
            type: file.type,
            data: file,
          });
        } catch (err) {
          logger.warn("Failed to add file from drop", err);
          const msg = err instanceof Error ? err.message : "Failed to add file";
          toast.error(msg);
        }
      });
    }
  }, [uppy, toast]);

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        try {
          uppy.addFile({
            source: "react-file-input",
            name: file.name,
            type: file.type,
            data: file,
          });
        } catch (err) {
          logger.warn("Failed to add file from input", err);
          const msg = err instanceof Error ? err.message : "Failed to add file";
          toast.error(msg);
        }
      });
    }
    // Reset input value so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [uppy, toast]);

  return (
    <div className={containerClasses}>
      {mode === "edit" ? (
        canUpload ? (
          <div
            className={`${dropzoneClasses} ${isDragging ? dropzoneActiveClasses : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                fileInputRef.current?.click();
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept={allowedFileTypes.join(",")}
            />
            <UploadSimple className={dropzoneIconClasses} weight="light" />
            <div className="text-center">
              <p className={dropzoneTextClasses}>
                Click to upload or drag files
              </p>
              <p className={dropzoneSubtextClasses}>
                PDF, images, DOC/DOCX, ZIP up to 25 MB
              </p>
            </div>
          </div>
        ) : (
          <div className={emptyClasses}>
            {t("workspaces.lot_detail.documents_create_first", "Create the lot to upload documents.")}
          </div>
        )
      ) : null}

      {mode === "edit" && queuedFiles.length > 0 ? (
        <div className={queueClasses}>
          <div className={queueHeaderClasses}>
            <div className="font-semibold text-slate-900 dark:text-slate-100">Pending uploads</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Set a document type for each file before uploading.
            </div>
          </div>
          <ul className={queueListClasses}>
            {queuedFiles.map((file) => (
              <li key={file.id} className={queueRowClasses}>
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <FilePreview
                    file={file.data instanceof Blob ? file.data : undefined}
                    name={file.name}
                    mimeType={file.type}
                  />
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <div className="truncate font-medium text-sm text-slate-900 dark:text-slate-200">{file.name}</div>
                    <div className="text-xs text-slate-500 font-mono dark:text-slate-500">{formatBytes(file.size || 0)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <span className="hidden sm:inline">Type</span>
                    <div className="relative">
                      <select
                        className={selectClasses}
                        value={
                          (file.meta.documentType as CreateLotDocumentUploadRequest["document_type"]) ||
                          lastSelectedType
                        }
                        onChange={(e) =>
                          handleQueuedTypeChange(
                            file.id,
                            e.target.value as CreateLotDocumentUploadRequest["document_type"],
                          )
                        }
                      >
                        {documentTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </label>
                  <button
                    type="button"
                    className={ghostButtonClasses}
                    onClick={() => handleRemoveQueuedFile(file.id)}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {mode === "edit" ? (
        <div className="flex justify-end">
          <button
            type="button"
            className={primaryButtonClasses}
            onClick={handleUpload}
            disabled={uploading || queuedFiles.length === 0 || !canUpload}
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      ) : null}

      {Object.keys(uploadProgress).length > 0 ? (
        <div className={progressClasses} ref={progressPanelRef}>
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 dark:bg-slate-800 dark:border-slate-700">
            <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">Uploads in progress</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Keep this tab open until uploads finish.</div>
          </div>
          <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto dark:divide-slate-800">
            {Object.entries(uploadProgress).map(([fileId, info]) => (
              <li key={fileId} className="p-4">
                <div className="flex justify-between text-xs mb-1">
                  <span className="truncate font-medium text-slate-700 pr-2 dark:text-slate-300">{info.name}</span>
                  <span className="text-slate-500 tabular-nums dark:text-slate-400">{info.percent}%</span>
                </div>
                <div
                  className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden dark:bg-slate-700"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={info.percent}
                >
                  <div className="h-full bg-sky-500 transition-all duration-200" style={{ width: `${info.percent}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {error ? <div className={errorClasses}>{error}</div> : null}

      {loading ? <div className={loadingClasses}>Loading documents…</div> : null}

      {!loading && items.length === 0 ? (
        <div className={emptyClasses}>No documents yet.</div>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className={listContainerClasses}>
          <ul className={listInnerClasses}>
            {items.map((item) => {
              const isDoc = (item as LotDocument).lot_id !== undefined;
              const name = item.filename || item.attachment_key;
              const byteSize = (item as LotDocument).file?.byte_size || (item as LotDraftFile).file?.byte_size;
              const mimeType = (item as LotDocument).file?.mime_type || (item as LotDraftFile).file?.mime_type;
              return (
                <li key={item.id} className={fileRowClasses}>
                  <div className={fileMetaClasses}>
                    <FileIcon name={name} mimeType={mimeType} />
                    <div className="flex flex-col min-w-0 gap-0.5">
                      <div className="truncate font-semibold text-slate-900 dark:text-slate-100">{name}</div>
                      <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500 dark:text-slate-400">
                        <span className={typeBadgeClasses}>
                          {item.document_type.replace("_", " ")}
                        </span>
                        <span>{formatDateTime(item.uploaded_at)}</span>
                        {byteSize ? (
                          <>
                            <span>•</span>
                            <span>{formatBytes(byteSize)}</span>
                          </>
                        ) : null}
                        {isDoc && (item as LotDocument).revision ? (
                          <>
                            <span>•</span>
                            <span>rev {(item as LotDocument).revision}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {renderActions(item)}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(docToDelete)}
        title={t(
          "workspaces.lot_detail.documents_delete_confirm",
          "Delete this document? This cannot be undone."
        )}
        description={
          docToDelete?.filename
            ? t("workspaces.lot_detail.documents_delete_named", {
                name: docToDelete.filename,
                defaultValue: "This will permanently remove \"{{name}}\"."
              })
            : undefined
        }
        confirmLabel={t("actions.delete", "Delete")}
        cancelLabel={t("actions.cancel")}
        confirmTone="danger"
        confirmLoading={Boolean(docToDelete && deletingDocId === docToDelete.id)}
        onCancel={() => {
          if (deletingDocId) return;
          setDocToDelete(null);
        }}
        onConfirm={async () => {
          if (docToDelete) {
            await handleDelete(docToDelete);
          }
        }}
      />
    </div>
  );
}
